'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface FabricType {
  id: string;
  name: string;
  icon: string;
  image: string;
  alt: string;
  description: string;
  washTemp: string;
  dryMethod: string;
  ironTemp: string;
  specialCare: string[];
}

interface FabricCareGuideProps {
  className?: string;
}

const FabricCareGuide = ({ className = '' }: FabricCareGuideProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedFabric, setSelectedFabric] = useState<string>('cotton');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const fabricTypes: FabricType[] = [
    {
      id: 'cotton',
      name: 'Kain Kapas',
      icon: 'SparklesIcon',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c573d3c4-1767910179092.png',
      alt: 'Close-up of white cotton fabric texture showing soft natural fibers',
      description: 'Kain yang paling biasa dan mudah dijaga. Sesuai untuk pakaian harian.',
      washTemp: '40-60°C',
      dryMethod: 'Mesin pengering atau jemur',
      ironTemp: 'Panas tinggi (200°C)',
      specialCare: [
        'Boleh dibasuh dengan mesin',
        'Tahan lasak dan mudah dijaga',
        'Mungkin mengecut sedikit selepas basuhan pertama',
        'Sesuai untuk pemutihan (jika putih)',
      ],
    },
    {
      id: 'silk',
      name: 'Kain Sutera',
      icon: 'StarIcon',
      image: 'https://images.unsplash.com/photo-1606941036724-54c5ca092cf5',
      alt: 'Luxurious pink silk fabric with smooth shiny surface and elegant drape',
      description: 'Kain mewah yang memerlukan penjagaan istimewa dan lembut.',
      washTemp: '30°C atau basuh tangan',
      dryMethod: 'Jemur dalam teduhan',
      ironTemp: 'Panas rendah (110°C)',
      specialCare: [
        'Basuh tangan atau dry clean',
        'Jangan guna pemutih',
        'Keringkan dalam teduhan',
        'Seterika dalam keadaan lembap',
      ],
    },
    {
      id: 'wool',
      name: 'Kain Bulu',
      icon: 'CloudIcon',
      image: 'https://images.unsplash.com/photo-1598964289041-685ec5cea088',
      alt: 'Soft gray wool fabric showing natural texture and warm fuzzy surface',
      description: 'Kain hangat yang memerlukan penjagaan khas untuk elak mengecut.',
      washTemp: '30°C dengan detergen khas',
      dryMethod: 'Jemur rata',
      ironTemp: 'Panas sederhana (150°C)',
      specialCare: [
        'Guna detergen khas untuk wool',
        'Jangan pulas atau perah',
        'Keringkan dalam bentuk rata',
        'Simpan dengan moth repellent',
      ],
    },
    {
      id: 'polyester',
      name: 'Kain Polyester',
      icon: 'BoltIcon',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_14f0c7f58-1765890992651.png',
      alt: 'Smooth blue polyester fabric with wrinkle-resistant synthetic texture',
      description: 'Kain sintetik yang tahan lasak dan mudah dijaga.',
      washTemp: '40°C',
      dryMethod: 'Mesin pengering suhu rendah',
      ironTemp: 'Panas rendah (110°C)',
      specialCare: [
        'Cepat kering dan tidak mudah kedut',
        'Tahan warna yang baik',
        'Boleh dicampur dengan kain lain',
        'Elakkan suhu tinggi',
      ],
    },
    {
      id: 'linen',
      name: 'Kain Linen',
      icon: 'SunIcon',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_113264c56-1764904747550.png',
      alt: 'Natural beige linen fabric with visible weave texture and breathable appearance',
      description: 'Kain sejuk dan selesa, sesuai untuk cuaca panas.',
      washTemp: '40-60°C',
      dryMethod: 'Jemur atau mesin pengering',
      ironTemp: 'Panas tinggi (200°C)',
      specialCare: [
        'Mudah kedut tetapi breathable',
        'Boleh dibasuh dengan suhu tinggi',
        'Seterika dalam keadaan lembap',
        'Menjadi lebih lembut selepas beberapa basuhan',
      ],
    },
    {
      id: 'denim',
      name: 'Kain Denim',
      icon: 'ShieldCheckIcon',
      image: 'https://images.unsplash.com/photo-1688666993112-bd6b02dd20a2',
      alt: 'Dark blue denim fabric showing classic jean material with diagonal twill weave',
      description: 'Kain tahan lasak untuk jeans dan pakaian kasual.',
      washTemp: '30-40°C',
      dryMethod: 'Jemur atau mesin pengering',
      ironTemp: 'Panas tinggi (200°C)',
      specialCare: [
        'Basuh terbalik untuk kekalkan warna',
        'Jangan basuh terlalu kerap',
        'Boleh air dry untuk kekalkan bentuk',
        'Warna mungkin luntur pada basuhan pertama',
      ],
    },
  ];

  if (!isHydrated) {
    return (
      <section className={`py-16 bg-muted ${className}`}>
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-10 bg-card rounded w-1/2 mx-auto mb-12"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-24 bg-card rounded-lg"></div>
              ))}
            </div>
            <div className="h-96 bg-card rounded-xl"></div>
          </div>
        </div>
      </section>
    );
  }

  const currentFabric = fabricTypes.find((f) => f.id === selectedFabric) || fabricTypes[0];

  return (
    <section className={`py-16 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-textPrimary mb-4">
            Panduan Penjagaan Kain
          </h2>
          <p className="text-lg text-textSecondary font-body max-w-2xl mx-auto">
            Ketahui cara terbaik untuk menjaga setiap jenis kain. Kami pakar dalam semua jenis
            fabrik.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {fabricTypes.map((fabric) => (
            <button
              key={fabric.id}
              onClick={() => setSelectedFabric(fabric.id)}
              className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                selectedFabric === fabric.id
                  ? 'border-primary bg-primary/10 shadow-md'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <Icon
                name={fabric.icon as any}
                size={32}
                variant={selectedFabric === fabric.id ? 'solid' : 'outline'}
                className={
                  selectedFabric === fabric.id
                    ? 'text-primary mx-auto mb-2'
                    : 'text-textSecondary mx-auto mb-2'
                }
              />

              <div className="text-sm font-body font-semibold text-textPrimary text-center">
                {fabric.name}
              </div>
            </button>
          ))}
        </div>

        <div className="bg-card rounded-xl shadow-lg overflow-hidden border border-border">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="relative h-64 md:h-auto overflow-hidden">
              <AppImage
                src={currentFabric.image}
                alt={currentFabric.alt}
                className="w-full h-full object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-heading font-bold text-white mb-2">
                  {currentFabric.name}
                </h3>
                <p className="text-white/90 font-body">{currentFabric.description}</p>
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 rounded-lg p-3 flex-shrink-0">
                    <Icon name="FireIcon" size={24} variant="outline" className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-body font-semibold text-textPrimary mb-1">
                      Suhu Basuhan
                    </h4>
                    <p className="text-textSecondary font-body">{currentFabric.washTemp}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 rounded-lg p-3 flex-shrink-0">
                    <Icon name="SunIcon" size={24} variant="outline" className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-body font-semibold text-textPrimary mb-1">
                      Kaedah Pengeringan
                    </h4>
                    <p className="text-textSecondary font-body">{currentFabric.dryMethod}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 rounded-lg p-3 flex-shrink-0">
                    <Icon name="BoltIcon" size={24} variant="outline" className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-body font-semibold text-textPrimary mb-1">
                      Suhu Seterika
                    </h4>
                    <p className="text-textSecondary font-body">{currentFabric.ironTemp}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <h4 className="text-sm font-body font-semibold text-textPrimary mb-3">
                    Penjagaan Khas
                  </h4>
                  <ul className="space-y-2">
                    {currentFabric.specialCare.map((care, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Icon
                          name="CheckCircleIcon"
                          size={20}
                          variant="solid"
                          className="text-success flex-shrink-0 mt-0.5"
                        />
                        <span className="text-sm text-textSecondary font-body">{care}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FabricCareGuide;
